import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import HomePage from "./pages/storefront/HomePage";
import ProductsPage from "./pages/storefront/ProductsPage";
import ProductDetailPage from "./pages/storefront/ProductDetailPage";
import AboutPage from "./pages/storefront/AboutPage";
import ContactPage from "./pages/storefront/ContactPage";
import NewsPage from "./pages/storefront/NewsPage";
import LoginPage from "./pages/auth/LoginPage";
import OemPrivateLabelPage from "./pages/storefront/OemPrivateLabelPage";
import SolutionsPage from "./pages/storefront/SolutionsPage";
import AdminDashboardPage from "./pages/admin/dashboard/Page";
import StorefrontHeader from "./components/StorefrontHeader";
import StorefrontFooter from "./pages/shared/StorefrontFooter";
import { getDefaultNavItems, normalizeNavTree } from "./pages/storefront/storefrontData";
import { setLang, STOREFRONT_SUPPORTED_LOCALES, t } from "./i18n";

const ADMIN_AUTH_UPDATED_EVENT = "ft-admin-auth-updated";
const USER_AUTH_UPDATED_EVENT = "ft-user-auth-updated";
const STOREFRONT_CONFIG_UPDATED_EVENT = "storefront-config-updated";
const STOREFRONT_CONFIG_UPDATED_AT_KEY = "ft_storefront_config_updated_at";
const STOREFRONT_THEME_TEMPLATES = new Set(["style-1", "style-2", "style-3"]);
const DEFAULT_STOREFRONT_THEME_TEMPLATE = "style-1";
const DEFAULT_STOREFRONT_STORE_ID = "default";
const STOREFRONT_HOME_MODULE_KEYS = [
  "intro",
  "quick-nav",
  "categories",
  "best-selling",
  "featured",
  "solutions",
  "scenarios",
  "story",
  "process",
  "inquiry"
];
const STOREFRONT_CATALOG_SECTION_KEYS = ["filters", "hero", "productGrid", "pagination"];
const STOREFRONT_DETAIL_SECTION_KEYS = ["media", "variations", "pricing", "description", "specs", "actions", "trustNote", "content"];
const DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG = {
  header: {
    height: 78,
    navAlign: "center",
    navButtonStyle: "pill"
  },
  home: {
    moduleOrder: STOREFRONT_HOME_MODULE_KEYS
  },
  catalog: {
    cardColumns: 3,
    sidebarMode: "left"
  },
  detail: {
    layoutMode: "split"
  }
};
const DEFAULT_STOREFRONT_THEME_PAGE_CONFIGS = {
  home: {
    heroEyebrow: "Global Sourcing Support",
    heroTitle: "Your Reliable Partner for Product Sourcing and Customization",
    heroSubtitle: "We support global buyers with product sourcing, customization, sampling, and order follow-up through clear communication and practical cooperation.",
    primaryCtaText: "View Products",
    secondaryCtaText: "Contact Us",
    heroImage: "/assets/about/1.png"
  },
  catalog: {
    heroTitle: "All Products",
    heroDescription: "Browse product categories, compare options, and contact our team for wholesale quotations.",
    emptyMessage: "No products found in this category.",
    ctaText: "Contact Now"
  },
  detail: {
    inquiryTitle: "Request a Sample or Quote",
    inquiryDescription: "Share your target quantity, branding request, and destination market for a tailored reply.",
    primaryCtaText: "Contact Us",
    secondaryCtaText: "Request Quote",
    trustNote: "Private label and export support available"
  }
};
const STAFF_ROLES = new Set(["developer", "super-admin", "admin"]);
const STOREFRONT_LOCALE_KEY = "ft_storefront_locale";
const DEVELOPER_ENTRY_SEGMENT = ["i", "n", "s", "t", "a", "l", "l"].join("");
const DEVELOPER_ENTRY_PATH = `/${DEVELOPER_ENTRY_SEGMENT}`;
const DEVELOPER_ENTRY_SUCCESS_PATH = `${DEVELOPER_ENTRY_PATH}/success`;
const DEVELOPER_ACCESS_SESSION_KEY = "ft_dev_access_verified";
const DEVELOPER_SETUP_SUCCESS_KEY = "ft_dev_setup_success";
const SetupPage = lazy(() => import("./pages/system/InstallPage"));
const SetupCompletePage = lazy(() => import("./pages/system/InstallSuccessPage"));

function normalizeStorefrontThemeTemplate(value) {
  const normalized = String(value || "").trim();
  return STOREFRONT_THEME_TEMPLATES.has(normalized) ? normalized : DEFAULT_STOREFRONT_THEME_TEMPLATE;
}

function applyStorefrontThemeTemplate(themeTemplate) {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.setAttribute("data-storefront-theme", normalizeStorefrontThemeTemplate(themeTemplate));
}

function normalizeStorefrontThemeLayoutConfig(value) {
  const source = value && typeof value === "object" ? value : {};

  const normalizeHomeModuleOrder = (order) => {
    const normalized = [];
    const sourceOrder = Array.isArray(order) ? order : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.home.moduleOrder;

    sourceOrder.forEach((item) => {
      const key = String(item || "").trim();
      if (!STOREFRONT_HOME_MODULE_KEYS.includes(key)) {
        return;
      }
      if (!normalized.includes(key)) {
        normalized.push(key);
      }
    });

    STOREFRONT_HOME_MODULE_KEYS.forEach((key) => {
      if (!normalized.includes(key)) {
        normalized.push(key);
      }
    });

    return normalized;
  };

  const headerHeightRaw = Number(source?.header?.height);
  const headerHeight = Number.isFinite(headerHeightRaw)
    ? Math.max(60, Math.min(110, Math.round(headerHeightRaw)))
    : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.header.height;
  const navAlignRaw = String(source?.header?.navAlign || DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.header.navAlign).trim();
  const navButtonStyleRaw = String(source?.header?.navButtonStyle || DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.header.navButtonStyle).trim();
  const navAlign = ["left", "center", "space-between"].includes(navAlignRaw)
    ? navAlignRaw
    : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.header.navAlign;
  const navButtonStyle = ["pill", "square", "underline"].includes(navButtonStyleRaw)
    ? navButtonStyleRaw
    : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.header.navButtonStyle;

  const cardColumnsRaw = Number(source?.catalog?.cardColumns);
  const cardColumns = Number.isFinite(cardColumnsRaw)
    ? Math.max(1, Math.min(4, Math.round(cardColumnsRaw)))
    : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.catalog.cardColumns;
  const sidebarModeRaw = String(source?.catalog?.sidebarMode || DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.catalog.sidebarMode).trim();
  const sidebarMode = ["left", "top"].includes(sidebarModeRaw)
    ? sidebarModeRaw
    : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.catalog.sidebarMode;

  const detailLayoutRaw = String(source?.detail?.layoutMode || DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.detail.layoutMode).trim();
  const detailLayoutMode = ["split", "stacked", "media-right"].includes(detailLayoutRaw)
    ? detailLayoutRaw
    : DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG.detail.layoutMode;

  return {
    header: {
      height: headerHeight,
      navAlign,
      navButtonStyle
    },
    home: {
      moduleOrder: normalizeHomeModuleOrder(source?.home?.moduleOrder)
    },
    catalog: {
      cardColumns,
      sidebarMode
    },
    detail: {
      layoutMode: detailLayoutMode
    }
  };
}

function resolveEffectiveStorefrontThemeLayoutConfig(storeSettings) {
  const settings = storeSettings && typeof storeSettings === "object" ? storeSettings : {};
  const activeCustomTemplateId = String(settings.activeCustomThemeTemplateId || "").trim();
  const customTemplates = Array.isArray(settings.customThemeTemplates) ? settings.customThemeTemplates : [];
  const activeTemplate = customTemplates.find((item) => String(item?.id || "").trim() === activeCustomTemplateId);
  if (activeTemplate?.themeLayoutConfig && typeof activeTemplate.themeLayoutConfig === "object") {
    return normalizeStorefrontThemeLayoutConfig(activeTemplate.themeLayoutConfig);
  }
  return normalizeStorefrontThemeLayoutConfig(settings.themeLayoutConfig);
}

function normalizeStorefrontThemePageConfigs(value) {
  const source = value && typeof value === "object" ? value : {};
  const readText = (pageKey, fieldKey, maxLength) => {
    const fallbackValue = String(DEFAULT_STOREFRONT_THEME_PAGE_CONFIGS?.[pageKey]?.[fieldKey] || "");
    const rawValue = String(source?.[pageKey]?.[fieldKey] || fallbackValue).trim();
    return rawValue.slice(0, maxLength) || fallbackValue;
  };

  const normalizeHomeModuleVisibility = () => {
    const sourceVisibility = source?.home?.moduleVisibility && typeof source.home.moduleVisibility === "object"
      ? source.home.moduleVisibility
      : {};
    return STOREFRONT_HOME_MODULE_KEYS.reduce((visibility, key) => {
      visibility[key] = sourceVisibility[key] !== false;
      return visibility;
    }, {});
  };

  const normalizeHomeModules = () => {
    const sourceModules = source?.home?.modules && typeof source.home.modules === "object"
      ? source.home.modules
      : {};
    return STOREFRONT_HOME_MODULE_KEYS.reduce((modules, key) => {
      const moduleSource = sourceModules?.[key] && typeof sourceModules[key] === "object" ? sourceModules[key] : {};
      modules[key] = {
        title: String(moduleSource.title || "").trim().slice(0, 160),
        subtitle: String(moduleSource.subtitle || "").trim().slice(0, 360),
        primaryCtaText: String(moduleSource.primaryCtaText || "").trim().slice(0, 48),
        secondaryCtaText: String(moduleSource.secondaryCtaText || "").trim().slice(0, 48),
        imageUrl: String(moduleSource.imageUrl || "").trim().slice(0, 600)
      };
      return modules;
    }, {});
  };

  const normalizePageSectionVisibility = (pageKey, sectionKeys) => {
    const sourceVisibility = source?.[pageKey]?.sectionVisibility && typeof source[pageKey].sectionVisibility === "object"
      ? source[pageKey].sectionVisibility
      : {};
    return sectionKeys.reduce((visibility, key) => {
      visibility[key] = sourceVisibility[key] !== false;
      return visibility;
    }, {});
  };

  return {
    home: {
      heroEyebrow: readText("home", "heroEyebrow", 80),
      heroTitle: readText("home", "heroTitle", 180),
      heroSubtitle: readText("home", "heroSubtitle", 420),
      primaryCtaText: readText("home", "primaryCtaText", 48),
      secondaryCtaText: readText("home", "secondaryCtaText", 48),
      heroImage: readText("home", "heroImage", 600),
      moduleVisibility: normalizeHomeModuleVisibility(),
      modules: normalizeHomeModules()
    },
    catalog: {
      heroTitle: readText("catalog", "heroTitle", 120),
      heroDescription: readText("catalog", "heroDescription", 280),
      emptyMessage: readText("catalog", "emptyMessage", 160),
      ctaText: readText("catalog", "ctaText", 48),
      sectionVisibility: normalizePageSectionVisibility("catalog", STOREFRONT_CATALOG_SECTION_KEYS)
    },
    detail: {
      inquiryTitle: readText("detail", "inquiryTitle", 120),
      inquiryDescription: readText("detail", "inquiryDescription", 320),
      primaryCtaText: readText("detail", "primaryCtaText", 48),
      secondaryCtaText: readText("detail", "secondaryCtaText", 48),
      trustNote: readText("detail", "trustNote", 120),
      sectionVisibility: normalizePageSectionVisibility("detail", STOREFRONT_DETAIL_SECTION_KEYS)
    }
  };
}

function resolveEffectiveStorefrontThemePageConfigs(storeSettings) {
  const settings = storeSettings && typeof storeSettings === "object" ? storeSettings : {};
  const activeCustomTemplateId = String(settings.activeCustomThemeTemplateId || "").trim();
  const customTemplates = Array.isArray(settings.customThemeTemplates) ? settings.customThemeTemplates : [];
  const activeTemplate = customTemplates.find((item) => String(item?.id || "").trim() === activeCustomTemplateId);
  if (activeTemplate?.themePageConfigs && typeof activeTemplate.themePageConfigs === "object") {
    return normalizeStorefrontThemePageConfigs(activeTemplate.themePageConfigs);
  }
  return normalizeStorefrontThemePageConfigs(settings.themePageConfigs);
}

function applyActiveThemeManifest(manifest) {
  if (typeof document === "undefined") {
    return;
  }

  const linkId = "storefront-theme-asset-css";
  const existing = document.getElementById(linkId);
  const href = String(manifest?.globalCssFile || "").trim();
  if (!href) {
    if (existing) {
      existing.remove();
    }
    return;
  }

  if (existing?.getAttribute("href") === href) {
    return;
  }

  if (existing) {
    existing.remove();
  }

  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function applyStorefrontThemeLayoutConfig(themeLayoutConfig) {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  const normalized = normalizeStorefrontThemeLayoutConfig(themeLayoutConfig);

  root.style.setProperty("--header-height", `${normalized.header.height}px`);
  root.setAttribute("data-storefront-nav-align", normalized.header.navAlign);
  root.setAttribute("data-storefront-nav-button-style", normalized.header.navButtonStyle);
  root.setAttribute("data-storefront-catalog-sidebar-mode", normalized.catalog.sidebarMode);
  root.setAttribute("data-storefront-detail-layout-mode", normalized.detail.layoutMode);
  root.style.setProperty("--storefront-catalog-columns", String(normalized.catalog.cardColumns));

  const orderMap = new Map(normalized.home.moduleOrder.map((key, index) => [key, index + 1]));
  STOREFRONT_HOME_MODULE_KEYS.forEach((key) => {
    root.style.setProperty(`--home-order-${key}`, String(orderMap.get(key) || 99));
  });
}

function readSessionFlag(key) {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sessionStorage.getItem(key) === "1";
}

function writeSessionFlag(key, enabled) {
  if (typeof window === "undefined") {
    return;
  }

  if (enabled) {
    window.sessionStorage.setItem(key, "1");
  } else {
    window.sessionStorage.removeItem(key);
  }
}

function normalizeAdminRoleValue(role) {
  const normalized = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

  if (["developer", "dev", "root", "owner", "system"].includes(normalized)) {
    return "developer";
  }

  if (normalized === "superadmin") {
    return "super-admin";
  }

  if (["super-admin", "super administrator", "sa"].includes(normalized)) {
    return "super-admin";
  }

  if (["admin", "administrator", "manager", "staff", "operator", "ops"].includes(normalized)) {
    return "admin";
  }

  return normalized;
}

function readStoredAdminAuthSnapshot() {
  if (typeof window === "undefined") {
    return { token: "", role: "", expiresAt: "" };
  }

  return {
    token: localStorage.getItem("ft_admin_token") || "",
    role: localStorage.getItem("ft_admin_role") || "",
    expiresAt: localStorage.getItem("ft_admin_expires_at") || ""
  };
}

function normalizeToSupportedStorefrontLocale(locale) {
  const raw = String(locale || "").trim().toLowerCase().replace(/_/g, "-");
  if (!raw) {
    return "";
  }

  if (STOREFRONT_SUPPORTED_LOCALES.has(raw)) {
    return raw;
  }

  const base = raw.split("-")[0];
  if (STOREFRONT_SUPPORTED_LOCALES.has(base)) {
    return base;
  }

  return "";
}

function detectStorefrontLocaleFromEnvironment() {
  if (typeof window === "undefined") {
    return "en";
  }

  const candidates = [];
  const nav = window.navigator || {};

  if (Array.isArray(nav.languages)) {
    candidates.push(...nav.languages);
  }

  candidates.push(nav.language, nav.userLanguage, nav.browserLanguage, nav.systemLanguage);

  try {
    const intlLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    candidates.push(intlLocale);
  } catch (_error) {
    // Ignore Intl locale probe errors and continue with navigator values.
  }

  for (const candidate of candidates) {
    const normalized = normalizeToSupportedStorefrontLocale(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "en";
}

function resolveInitialStorefrontLocale() {
  if (typeof window === "undefined") {
    return "en";
  }

  const stored = normalizeToSupportedStorefrontLocale(localStorage.getItem(STOREFRONT_LOCALE_KEY));
  if (stored) {
    return stored;
  }

  return detectStorefrontLocaleFromEnvironment();
}

const ADMIN_UI_TEXT = {
  en: {
    loggedOut: "You have logged out.",
    expired: "Session expired. Please login again.",
    restricted: "This account is restricted and cannot access the admin panel. Please contact an administrator.",
    renewed: "Session renewed successfully.",
    expiringTitle: "Session Expiring Soon",
    expiringBody: "Your admin session will expire in less than 5 minutes.",
    renewing: "Renewing...",
    extend: "Extend Session",
    logout: "Logout"
  },
  zh: {
    loggedOut: "您已退出登录。",
    expired: "会话已过期，请重新登录。",
    restricted: "账号被限制，无法进入管理后台，请联系管理员。",
    renewed: "会话续期成功。",
    expiringTitle: "会话即将过期",
    expiringBody: "您的后台会话将在 5 分钟内过期。",
    renewing: "续期中...",
    extend: "延长会话",
    logout: "退出登录"
  }
};

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [adminToken, setAdminToken] = useState(
    () => localStorage.getItem("ft_admin_token") || ""
  );
  const [adminRole, setAdminRole] = useState(
    () => localStorage.getItem("ft_admin_role") || ""
  );
  const [adminLocale, setAdminLocale] = useState(
    () => localStorage.getItem("ft_admin_locale") || "zh"
  );
  const [adminExpiresAt, setAdminExpiresAt] = useState(
    () => localStorage.getItem("ft_admin_expires_at") || ""
  );
  const [userToken, setUserToken] = useState(
    () => localStorage.getItem("ft_user_token") || ""
  );
  const [storefrontLocale, setStorefrontLocale] = useState(() => resolveInitialStorefrontLocale());
  const [sessionNotice, setSessionNotice] = useState("");
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [developerAccessGranted, setDeveloperAccessGranted] = useState(() => readSessionFlag(DEVELOPER_ACCESS_SESSION_KEY));
  const [developerSetupComplete, setDeveloperSetupComplete] = useState(() => readSessionFlag(DEVELOPER_SETUP_SUCCESS_KEY));
  const lastAutoRenewAtRef = useRef(0);
  const [bootstrapStatus, setBootstrapStatus] = useState({
    loading: true,
    backendUnavailable: false,
    installed: true,
    installedAt: "",
    developer: null,
    sqlImport: null,
    mysql: null,
    redis: null,
    oss: null,
    smtp: null,
    jwt: null,
    apiServer: null,
    site: null
  });
  const [showBootstrapLoadingScreen, setShowBootstrapLoadingScreen] = useState(false);
  const [storefrontConfigRefreshVersion, setStorefrontConfigRefreshVersion] = useState(0);
  const [hasCustomStorefrontNav, setHasCustomStorefrontNav] = useState(false);
  const [storefrontNavSource, setStorefrontNavSource] = useState([]);
  const [storefrontBranding, setStorefrontBranding] = useState({
    storefrontTitle: "LOYA SPORT",
    logoUrl: "/assets/about/logo.png",
    logoAlt: "Loya Sport logo",
    topNoticeText: "",
    contactEmail: "",
    contactPhone: "",
    contactWhatsapp: ""
  });
  const [storefrontThemeLayoutConfig, setStorefrontThemeLayoutConfig] = useState(DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG);
  const [storefrontThemePageConfigs, setStorefrontThemePageConfigs] = useState(DEFAULT_STOREFRONT_THEME_PAGE_CONFIGS);
  const [activeThemeManifest, setActiveThemeManifest] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || "/api";
  const developerApiPaths = useMemo(() => ({
    status: `${apiBaseUrl}/site/bootstrap-status`,
    verify: `${apiBaseUrl}/developer/access/verify`,
    apply: `${apiBaseUrl}/system/bootstrap/apply`,
    importSql: `${apiBaseUrl}/system/bootstrap/import-data`
  }), [apiBaseUrl]);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isDeveloperEntryRoute = location.pathname.startsWith(DEVELOPER_ENTRY_PATH);
  const isDeveloperEntrySuccessRoute = location.pathname === DEVELOPER_ENTRY_SUCCESS_PATH;
  const isMediaAssetRoute = /^(?:\/media\/oss|\/uploads|\/api\/uploads|\/assets\/company-images|\/api\/assets\/company-images)(?:\/|$)/i.test(
    location.pathname
  );
  const isLoginRoute = location.pathname === "/login" || location.pathname === "/admin/login";
  const storedAdminAuth = readStoredAdminAuthSnapshot();
  const effectiveAdminToken = String(adminToken || storedAdminAuth.token || "").trim();
  const effectiveAdminRole = normalizeAdminRoleValue(adminRole || storedAdminAuth.role || "");
  const effectiveAdminExpiresAt = String(adminExpiresAt || storedAdminAuth.expiresAt || "").trim();
  const hasAdminAccess = Boolean(effectiveAdminToken) && STAFF_ROLES.has(effectiveAdminRole);
  const hasPendingAdminSync = Boolean(effectiveAdminToken) && !effectiveAdminRole;
  const hasStorefrontSession = Boolean(userToken);
  const isAuthenticated = hasAdminAccess || hasStorefrontSession;
  const uiText = isAdminRoute
    ? (ADMIN_UI_TEXT[adminLocale] || ADMIN_UI_TEXT.en)
    : ADMIN_UI_TEXT.en;
  if (isAdminRoute) {
    setLang(adminLocale || "zh");
  } else {
    setLang(STOREFRONT_SUPPORTED_LOCALES.has(storefrontLocale) ? storefrontLocale : "en");
  }

  const storefrontNavItems = useMemo(() => {
    if (hasCustomStorefrontNav && Array.isArray(storefrontNavSource) && storefrontNavSource.length) {
      return normalizeNavTree(storefrontNavSource);
    }

    return getDefaultNavItems();
  }, [hasCustomStorefrontNav, storefrontLocale, storefrontNavSource]);

  function navigateTo(pathname) {
    if (location.pathname !== pathname) {
      navigate(pathname);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  useEffect(() => {
    if (adminToken) {
      localStorage.setItem("ft_admin_token", adminToken);
    } else {
      localStorage.removeItem("ft_admin_token");
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminRole) {
      localStorage.setItem("ft_admin_role", adminRole);
    } else {
      localStorage.removeItem("ft_admin_role");
    }
  }, [adminRole]);

  useEffect(() => {
    if (adminLocale) {
      localStorage.setItem("ft_admin_locale", adminLocale);
    } else {
      localStorage.removeItem("ft_admin_locale");
    }
  }, [adminLocale]);

  useEffect(() => {
    if (adminExpiresAt) {
      localStorage.setItem("ft_admin_expires_at", adminExpiresAt);
    } else {
      localStorage.removeItem("ft_admin_expires_at");
    }
  }, [adminExpiresAt]);

  useEffect(() => {
    if (userToken) {
      localStorage.setItem("ft_user_token", userToken);
    } else {
      localStorage.removeItem("ft_user_token");
    }
  }, [userToken]);

  useEffect(() => {
    localStorage.setItem(STOREFRONT_LOCALE_KEY, storefrontLocale);
  }, [storefrontLocale]);

  useEffect(() => {
    writeSessionFlag(DEVELOPER_ACCESS_SESSION_KEY, developerAccessGranted);
  }, [developerAccessGranted]);

  useEffect(() => {
    writeSessionFlag(DEVELOPER_SETUP_SUCCESS_KEY, developerSetupComplete);
  }, [developerSetupComplete]);

  useEffect(() => {
    if (bootstrapStatus.installed && !developerSetupComplete) {
      setDeveloperAccessGranted(false);
    }
  }, [developerSetupComplete, bootstrapStatus.installed]);

  useEffect(() => {
    if (isAdminRoute || isDeveloperEntryRoute) {
      return;
    }

    const params = new URLSearchParams(location.search || "");
    const currentLangParam = String(params.get("lang") || "").trim().toLowerCase();
    const desiredLangParam = storefrontLocale === "en" ? "" : storefrontLocale;

    if ((desiredLangParam && currentLangParam === desiredLangParam) || (!desiredLangParam && !currentLangParam)) {
      return;
    }

    if (desiredLangParam) {
      params.set("lang", desiredLangParam);
    } else {
      params.delete("lang");
    }

    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash
      },
      { replace: true }
    );
  }, [
    isAdminRoute,
    isDeveloperEntryRoute,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    storefrontLocale
  ]);

  useEffect(() => {
    const isNoindexRoute =
      isAdminRoute || isDeveloperEntryRoute || location.pathname === "/login" || location.pathname === "/admin/login";

    const ensureMetaTag = (selector, attrName, attrValue, content) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    ensureMetaTag(
      'meta[name="robots"]',
      "name",
      "robots",
      isNoindexRoute
        ? "noindex,nofollow,noarchive"
        : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
    );

    if (isNoindexRoute) {
      return;
    }

    const normalizedPath = location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");
    const canonicalUrl = new URL(normalizedPath || "/", window.location.origin);
    if (storefrontLocale !== "en") {
      canonicalUrl.searchParams.set("lang", storefrontLocale);
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl.toString());

    document
      .querySelectorAll('link[rel="alternate"][data-seo-managed="true"]')
      .forEach((node) => node.remove());

    Array.from(STOREFRONT_SUPPORTED_LOCALES).forEach((locale) => {
      const alternateUrl = new URL(normalizedPath || "/", window.location.origin);
      if (locale !== "en") {
        alternateUrl.searchParams.set("lang", locale);
      }

      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", locale);
      link.setAttribute("href", alternateUrl.toString());
      link.setAttribute("data-seo-managed", "true");
      document.head.appendChild(link);
    });

    const xDefault = document.createElement("link");
    xDefault.setAttribute("rel", "alternate");
    xDefault.setAttribute("hreflang", "x-default");
    xDefault.setAttribute("href", new URL(normalizedPath || "/", window.location.origin).toString());
    xDefault.setAttribute("data-seo-managed", "true");
    document.head.appendChild(xDefault);
  }, [isAdminRoute, isDeveloperEntryRoute, location.pathname, storefrontLocale]);

  useEffect(() => {
    function syncAdminAuthState() {
      setAdminToken(localStorage.getItem("ft_admin_token") || "");
      setAdminRole(localStorage.getItem("ft_admin_role") || "");
      setAdminExpiresAt(localStorage.getItem("ft_admin_expires_at") || "");
      setUserToken(localStorage.getItem("ft_user_token") || "");
    }

    syncAdminAuthState();

    window.addEventListener(ADMIN_AUTH_UPDATED_EVENT, syncAdminAuthState);
    window.addEventListener(USER_AUTH_UPDATED_EVENT, syncAdminAuthState);
    window.addEventListener("storage", syncAdminAuthState);

    return () => {
      window.removeEventListener(ADMIN_AUTH_UPDATED_EVENT, syncAdminAuthState);
      window.removeEventListener(USER_AUTH_UPDATED_EVENT, syncAdminAuthState);
      window.removeEventListener("storage", syncAdminAuthState);
    };
  }, []);

  useEffect(() => {
    if (!effectiveAdminToken || !effectiveAdminRole) {
      return;
    }

    if (STAFF_ROLES.has(effectiveAdminRole)) {
      return;
    }

    localStorage.removeItem("ft_admin_token");
    localStorage.removeItem("ft_admin_role");
    localStorage.removeItem("ft_admin_expires_at");
    setAdminToken("");
    setAdminRole("");
    setAdminExpiresAt("");

    if (isAdminRoute) {
      navigate("/products", { replace: true });
    }
  }, [effectiveAdminRole, effectiveAdminToken, isAdminRoute, navigate]);

  useEffect(() => {
    if (!bootstrapStatus.loading) {
      setShowBootstrapLoadingScreen(false);
      return undefined;
    }

    const revealTimer = window.setTimeout(() => {
      setShowBootstrapLoadingScreen(true);
    }, 320);

    return () => {
      window.clearTimeout(revealTimer);
    };
  }, [bootstrapStatus.loading]);

  useEffect(() => {
    let cancelled = false;

    async function loadInstallStatus() {
      try {
        const response = await fetch(developerApiPaths.status);
        if (!response.ok) {
          throw new Error("Failed to load site bootstrap status");
        }
        const data = await response.json();
        if (!cancelled) {
          setBootstrapStatus({
            loading: false,
            backendUnavailable: false,
            installed: Boolean(data.installed),
            installedAt: data.installedAt || "",
            developer: data.developer || null,
            sqlImport: data.sqlImport || null,
            mysql: data.mysql || null,
            redis: data.redis || null,
            oss: data.oss || null,
            smtp: data.smtp || null,
            jwt: data.jwt || null,
            apiServer: data.apiServer || null,
            site: data.site || null
          });
        }
      } catch (_err) {
        if (!cancelled) {
          setBootstrapStatus({
            loading: false,
            backendUnavailable: true,
            installed: true,
            installedAt: "",
            developer: null,
            sqlImport: null,
            mysql: null,
            redis: null,
            oss: null,
            smtp: null,
            jwt: null,
            apiServer: null,
            site: null
          });
        }
      }
    }

    loadInstallStatus();

    return () => {
      cancelled = true;
    };
  }, [developerApiPaths.status]);

  useEffect(() => {
    function refreshStorefrontConfig() {
      setStorefrontConfigRefreshVersion((prev) => prev + 1);
    }

    function handleStorage(event) {
      if (event.key === STOREFRONT_CONFIG_UPDATED_AT_KEY) {
        refreshStorefrontConfig();
      }
    }

    window.addEventListener(STOREFRONT_CONFIG_UPDATED_EVENT, refreshStorefrontConfig);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(STOREFRONT_CONFIG_UPDATED_EVENT, refreshStorefrontConfig);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadStorefrontNavMenu() {
      try {
        const response = await fetch(`${apiBaseUrl}/storefront/config`);
        if (!response.ok) {
          throw new Error("Failed to load storefront config");
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        if (Array.isArray(data.navMenu) && data.navMenu.length) {
          setHasCustomStorefrontNav(true);
          setStorefrontNavSource(data.navMenu);
        } else {
          setHasCustomStorefrontNav(false);
          setStorefrontNavSource([]);
        }

        const storeSettings = data?.storeSettings && typeof data.storeSettings === "object"
          ? data.storeSettings
          : null;
        const resolvedThemePageConfigs = resolveEffectiveStorefrontThemePageConfigs(storeSettings);
        const resolvedThemeLayoutConfig = resolveEffectiveStorefrontThemeLayoutConfig(storeSettings);
        applyStorefrontThemeTemplate(storeSettings?.themeTemplate);
        applyStorefrontThemeLayoutConfig(resolvedThemeLayoutConfig);
        applyActiveThemeManifest(data?.activeThemeManifest || null);
        setStorefrontThemeLayoutConfig(resolvedThemeLayoutConfig);
        setStorefrontThemePageConfigs(resolvedThemePageConfigs);
        setActiveThemeManifest(data?.activeThemeManifest || null);
        setStorefrontBranding({
          storefrontTitle: String(storeSettings?.storefrontTitle || "LOYA SPORT").trim() || "LOYA SPORT",
          logoUrl: String(storeSettings?.logoUrl || "/assets/about/logo.png").trim() || "/assets/about/logo.png",
          logoAlt: String(storeSettings?.logoAlt || "Loya Sport logo").trim() || "Loya Sport logo",
          topNoticeText: String(storeSettings?.topNoticeText || "").trim(),
          contactEmail: String(storeSettings?.contactEmail || "").trim(),
          contactPhone: String(storeSettings?.contactPhone || "").trim(),
          contactWhatsapp: String(storeSettings?.contactWhatsapp || "").trim()
        });
      } catch (_err) {
        if (!cancelled) {
          setHasCustomStorefrontNav(false);
          setStorefrontNavSource([]);
          setStorefrontBranding({
            storefrontTitle: "LOYA SPORT",
            logoUrl: "/assets/about/logo.png",
            logoAlt: "Loya Sport logo",
            topNoticeText: "",
            contactEmail: "",
            contactPhone: "",
            contactWhatsapp: ""
          });
          setStorefrontThemeLayoutConfig(DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG);
          setStorefrontThemePageConfigs(DEFAULT_STOREFRONT_THEME_PAGE_CONFIGS);
          setActiveThemeManifest(null);
          applyActiveThemeManifest(null);
          applyStorefrontThemeTemplate(DEFAULT_STOREFRONT_THEME_TEMPLATE);
          applyStorefrontThemeLayoutConfig(DEFAULT_STOREFRONT_THEME_LAYOUT_CONFIG);
        }
      }
    }

    loadStorefrontNavMenu();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, storefrontConfigRefreshVersion]);

  useEffect(() => {
    document.body.classList.toggle("admin-route", isAdminRoute);
    return () => {
      document.body.classList.remove("admin-route");
    };
  }, [isAdminRoute]);

  useEffect(() => {
    const normalizedPath = location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");
    const isInstallPath = normalizedPath === DEVELOPER_ENTRY_PATH || normalizedPath.startsWith(`${DEVELOPER_ENTRY_PATH}/`);
    const isInstallSuccessPath = normalizedPath === DEVELOPER_ENTRY_SUCCESS_PATH;
    const deferToContentTitle = normalizedPath === "/products" || normalizedPath.startsWith("/products/");

    if (deferToContentTitle) {
      return;
    }

    const brandTitle = t("metaTitleBrand");

    // Never expose install-route semantics in browser title when installation is finished.
    if (bootstrapStatus.installed && isInstallPath && !isInstallSuccessPath) {
      document.title = `Page Not Found | ${brandTitle}`;
      return;
    }

    let titleKey = "metaTitleHome";

    if (isInstallPath) {
      titleKey = "metaTitleSystemSetup";
    } else if (normalizedPath === "/login" || normalizedPath === "/admin/login") {
      titleKey = "metaTitleLogin";
    } else if (normalizedPath === "/admin") {
      titleKey = "metaTitleAdmin";
    } else if (normalizedPath === "/products") {
      titleKey = "metaTitleProducts";
    } else if (normalizedPath.startsWith("/products/")) {
      titleKey = "metaTitleProductDetail";
    } else if (normalizedPath === "/custom-service" || normalizedPath === "/oem-private-label") {
      titleKey = "metaTitleCustomService";
    } else if (normalizedPath === "/solutions") {
      titleKey = "metaTitleSolutions";
    } else if (normalizedPath === "/about") {
      titleKey = "metaTitleAbout";
    } else if (normalizedPath === "/contact") {
      titleKey = "metaTitleContact";
    } else if (normalizedPath === "/news" || normalizedPath === "/blog") {
      titleKey = "metaTitleBlog";
    }

    const pageTitle = t(titleKey);

    if (typeof pageTitle === "string" && pageTitle && pageTitle !== titleKey) {
      document.title = `${pageTitle} | ${brandTitle}`;
      return;
    }

    document.title = brandTitle;
  }, [adminLocale, bootstrapStatus.installed, location.pathname, storefrontLocale]);

  useEffect(() => {
    if (location.hash) {
      const hashId = decodeURIComponent(location.hash.replace(/^#/, "")).trim();
      if (hashId) {
        const anchor = document.getElementById(hashId);
        if (anchor) {
          anchor.scrollIntoView({ behavior: "auto", block: "start" });
          return;
        }
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!adminToken || !adminExpiresAt) {
      setShowSessionWarning(false);
      return;
    }

    const warningWindowMs = 5 * 60 * 1000;
    const timer = window.setInterval(() => {
      const remaining = new Date(adminExpiresAt).getTime() - Date.now();

      if (remaining <= 0) {
        handleSessionExpired();
        return;
      }

      if (remaining <= warningWindowMs) {
        setShowSessionWarning(true);
      }
    }, 30000);

    return () => window.clearInterval(timer);
  }, [adminToken, adminExpiresAt]);

  function handleAdminLogout() {
    setAdminToken("");
    setAdminRole("");
    setAdminExpiresAt("");
    setUserToken("");
    localStorage.removeItem("ft_user_token");
    localStorage.removeItem("ft_user_profile");
    setShowSessionWarning(false);
    setSessionNotice(uiText.loggedOut);
    navigateTo("/login");
  }

  function handleSessionExpired() {
    setAdminToken("");
    setAdminRole("");
    setAdminExpiresAt("");
    setUserToken("");
    localStorage.removeItem("ft_user_token");
    localStorage.removeItem("ft_user_profile");
    setShowSessionWarning(false);
    setSessionNotice(uiText.expired);
    localStorage.setItem("ft_post_login_redirect", `${location.pathname}${location.search || ""}${location.hash || ""}` || "/admin");
    navigateTo("/login");
  }
  function handleRestrictedAdminAccess() {
    setAdminToken("");
    setAdminRole("");
    setAdminExpiresAt("");
    setUserToken("");
    localStorage.removeItem("ft_user_token");
    localStorage.removeItem("ft_user_profile");
    setShowSessionWarning(false);
    setSessionNotice(uiText.restricted);
    localStorage.setItem("ft_post_login_redirect", "/admin");
    navigateTo("/login");
  }

  async function renewAdminSession(options = {}) {
    const silent = Boolean(options.silent);
    if (!adminToken) {
      return;
    }

    setRenewing(true);
    try {
      const response = await fetch(`${apiBaseUrl}/admin/session/refresh`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Session refresh failed");
      }

      setAdminToken(data.token);
      setAdminExpiresAt(data.expiresAt);
      setShowSessionWarning(false);
      if (!silent) {
        setSessionNotice(uiText.renewed);
      }
    } catch (_err) {
      handleSessionExpired();
    } finally {
      setRenewing(false);
    }
  }

  useEffect(() => {
    if (!adminToken || !adminExpiresAt || !isAdminRoute) {
      return undefined;
    }

    const renewThresholdMs = 10 * 60 * 1000;
    const renewCooldownMs = 2 * 60 * 1000;

    const maybeRenewSession = () => {
      const expiresAtMs = new Date(adminExpiresAt).getTime();
      if (!Number.isFinite(expiresAtMs)) {
        return;
      }

      const remaining = expiresAtMs - Date.now();
      if (remaining > renewThresholdMs) {
        return;
      }

      const now = Date.now();
      if (now - lastAutoRenewAtRef.current < renewCooldownMs) {
        return;
      }

      lastAutoRenewAtRef.current = now;
      void renewAdminSession({ silent: true });
    };

    const activityEvents = ["pointerdown", "keydown", "scroll", "mousemove"];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, maybeRenewSession, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, maybeRenewSession);
      });
    };
  }, [adminExpiresAt, adminToken, isAdminRoute]);

  if (bootstrapStatus.loading) {
    if (isDeveloperEntryRoute && !showBootstrapLoadingScreen) {
      return <div className="install-loading-screen install-loading-screen-pending" aria-hidden="true" />;
    }

    if (isDeveloperEntryRoute && !isDeveloperEntrySuccessRoute) {
      return (
        <div className="install-loading-screen">
          <div className="install-loading-card">
            <span className="install-loading-orb" aria-hidden="true" />
            <p className="install-kicker">404</p>
            <h1>Page not found</h1>
            <p>The requested path does not match any storefront route.</p>
          </div>
        </div>
      );
    }
  }

  function RouteLoadingOverlay() {
    return (
      <div className="install-loading-screen install-loading-overlay" role="status" aria-live="polite">
        <div className="install-loading-progress-shell">
          <div className="install-loading-progress-meta">
            <span className="install-loading-progress-brand">{storefrontBranding.storefrontTitle || "LOYA SPORT"}</span>
            <span className="install-loading-progress-copy">Refreshing page state...</span>
          </div>
          <div className="install-loading-progress-track" aria-hidden="true">
            <span className="install-loading-progress-bar" />
          </div>
        </div>
      </div>
    );
  }

  function InternalFaultPage({ title, detail }) {
    return (
      <div className="install-loading-screen">
        <div className="install-loading-card">
          <p className="install-kicker">LOYA SPORT</p>
          <h1>{title || "Internal Site Error"}</h1>
          <p>{detail || "The service is temporarily unavailable. Please wait a moment and try again."}</p>
        </div>
      </div>
    );
  }

  function NotFoundPage() {
    return (
      <div className="install-loading-screen">
        <div className="install-loading-card">
          <p className="install-kicker">404</p>
          <h1>Page not found</h1>
          <p>The requested path does not match any storefront route.</p>
        </div>
      </div>
    );
  }

  function DeveloperAccessPage() {
    const [authCode, setAuthCode] = useState("");
    const [phase, setPhase] = useState("idle");
    const [error, setError] = useState("");

    async function submit(event) {
      event.preventDefault();

      if (!String(authCode || "").trim()) {
        setError("请输入开发者授权码。");
        return;
      }

      setPhase("verifying");
      setError("");

      try {
        const response = await fetch(developerApiPaths.verify, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ authCode: String(authCode || "").trim() })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.verified !== true) {
          throw new Error(String(payload?.message || "开发者授权校验失败"));
        }

        setDeveloperAccessGranted(true);
      } catch (err) {
        setError(String(err?.message || "开发者授权校验失败"));
      } finally {
        setPhase("idle");
      }
    }

    return (
      <div className="install-loading-screen">
        <div className="install-loading-card">
          <p className="install-kicker">Developer Access</p>
          <h1>Restricted Setup Entry</h1>
          <p>此入口仅供网站开发者使用。请先完成授权校验，再进入系统初始化页面。</p>
          <form onSubmit={submit} style={{ width: "100%" }}>
            <label style={{ display: "grid", gap: 10, marginTop: 18 }}>
              <span>开发者授权码</span>
              <input
                type="password"
                value={authCode}
                onChange={(event) => setAuthCode(event.target.value)}
                autoComplete="off"
              />
            </label>
            <div className="row-actions" style={{ marginTop: 18 }}>
              <button type="submit" className="button" disabled={phase === "verifying"}>
                {phase === "verifying" ? "校验中..." : "验证并进入"}
              </button>
            </div>
          </form>
          {error ? <p style={{ marginTop: 14, color: "#b42318" }}>{error}</p> : null}
        </div>
      </div>
    );
  }

  function StorefrontMaintenancePage() {
    return (
      <div className="install-loading-screen">
        <div className="install-loading-card">
          <p className="install-kicker">Site Maintenance</p>
          <h1>网站维护中 / Website Under Maintenance</h1>
          <p>网站正在维护，请稍后再来。由此给您带来不便，深感抱歉。</p>
          <p>The site is currently under maintenance. Please come back later. We sincerely apologize for the inconvenience.</p>
        </div>
      </div>
    );
  }

  if (bootstrapStatus.backendUnavailable && !isDeveloperEntryRoute) {
    return (
      <InternalFaultPage
        title="Internal Site Error"
        detail="The site is temporarily unavailable due to a service issue. Please be patient and wait a moment."
      />
    );
  }

  if (bootstrapStatus.installed && isDeveloperEntryRoute && !isDeveloperEntrySuccessRoute) {
    return <NotFoundPage />;
  }

  const websiteEnabled = Boolean(bootstrapStatus?.site?.websiteEnabled ?? true);
  const registrationEnabled = Boolean(bootstrapStatus?.site?.allowUserRegistration ?? true);

  if (!isAdminRoute && !isDeveloperEntryRoute && !isLoginRoute && !websiteEnabled) {
    return <StorefrontMaintenancePage />;
  }

  function withBootstrapGuard(element) {
    if (!bootstrapStatus.installed) {
      return (
        <InternalFaultPage
          title="Site Not Initialized"
          detail="The site has not been initialized yet."
        />
      );
    }

    return element;
  }

  const hasLoginPageComponent = typeof LoginPage === "function";

  const whatsappDigits = String(storefrontBranding.contactWhatsapp || "")
    .replace(/[^\d]/g, "")
    .trim();
  const whatsappLink = whatsappDigits ? `https://wa.me/${whatsappDigits}` : "";

  return (
    <div className="site-shell">
      {/* 全局统一前台导航栏（含Logo/菜单/右侧工具） */}
      {!isAdminRoute && !isDeveloperEntryRoute && !isMediaAssetRoute && (
        <StorefrontHeader
          storefrontTitle={storefrontBranding.storefrontTitle || "LOYA SPORT"}
          logoSrc={"/assets/about/logo.png"}
          logoAlt={storefrontBranding.logoAlt || "Loya Sport logo"}
          navItems={storefrontNavItems}
          language={storefrontLocale}
          onChangeLanguage={(nextLocale) => {
            const normalized = String(nextLocale || "").trim().toLowerCase();
            if (!STOREFRONT_SUPPORTED_LOCALES.has(normalized)) {
              return;
            }
            setStorefrontLocale(normalized);
          }}
          currency={"USD"}
          effectiveShowPricing={true}
          isAuthenticated={isAuthenticated}
        />
      )}
      {!isAdminRoute && !isDeveloperEntryRoute && !isMediaAssetRoute ? (
        <div className="global-contact-bar">
          <div className="container global-contact-inner">
            <div className="global-contact-meta">
              <span className="global-contact-tagline">{storefrontBranding.topNoticeText || t("globalContactTagline")}</span>
              {storefrontBranding.contactEmail ? <span>{`✉ ${storefrontBranding.contactEmail}`}</span> : null}
              {storefrontBranding.contactPhone ? <span>{`☎ ${storefrontBranding.contactPhone}`}</span> : null}
            </div>
            {isAuthenticated ? (
              <button
                type="button"
                className="global-admin-entry"
                onClick={() => navigateTo(hasAdminAccess ? "/admin" : "/products")}
              >
                {t("accountCenter")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <Routes>
        <Route
          path={DEVELOPER_ENTRY_PATH}
          element={
            bootstrapStatus.installed ? (
              <Navigate to="/login" replace />
            ) : !developerAccessGranted ? (
              <DeveloperAccessPage />
            ) : (
              <Suspense fallback={<div className="install-loading-screen install-loading-screen-pending" aria-hidden="true" />}>
                <SetupPage
                  apiBaseUrl={apiBaseUrl}
                  apiEndpoints={developerApiPaths}
                  installStatus={bootstrapStatus}
                  onInstalled={(nextStatus) => {
                    const merged = { ...bootstrapStatus, ...nextStatus };
                    setBootstrapStatus({ loading: false, ...merged });
                    setDeveloperSetupComplete(true);
                    if (merged?.developer?.account || merged?.developer?.password) {
                      sessionStorage.setItem("ft_install_developer", JSON.stringify(merged.developer || {}));
                    }
                    navigate(DEVELOPER_ENTRY_SUCCESS_PATH, { replace: true });
                  }}
                />
              </Suspense>
            )
          }
        />
        <Route
          path={DEVELOPER_ENTRY_SUCCESS_PATH}
          element={
            bootstrapStatus.installed && developerSetupComplete ? (
              <Suspense fallback={<div className="install-loading-screen install-loading-screen-pending" aria-hidden="true" />}>
                <SetupCompletePage
                  installStatus={bootstrapStatus}
                  onContinue={() => {
                    sessionStorage.removeItem("ft_install_developer");
                    setDeveloperAccessGranted(false);
                    setDeveloperSetupComplete(false);
                    navigate("/login");
                  }}
                  onVisitHome={() => {
                    sessionStorage.removeItem("ft_install_developer");
                    setDeveloperAccessGranted(false);
                    setDeveloperSetupComplete(false);
                    navigate("/");
                  }}
                />
              </Suspense>
            ) : (
              <InternalFaultPage
                title="开发者流程未完成"
                detail="请使用开发者入口并先完成授权校验。"
              />
            )
          }
        />
        <Route
          path="/"
          element={withBootstrapGuard(
            <HomePage
              apiBaseUrl={apiBaseUrl}
              storefrontLocale={storefrontLocale}
              pageTemplateConfig={storefrontThemePageConfigs.home}
              themeLayoutConfig={storefrontThemeLayoutConfig}
            />
          )}
        />
        <Route
          path="/products"
          element={withBootstrapGuard(<ProductsPage apiBaseUrl={apiBaseUrl} navItems={storefrontNavItems} storefrontLocale={storefrontLocale} pageTemplateConfig={storefrontThemePageConfigs.catalog} />)}
        />
        <Route
          path="/products/:productId"
          element={withBootstrapGuard(
            <ProductDetailPage
              apiBaseUrl={apiBaseUrl}
              storefrontLocale={storefrontLocale}
              storefrontContacts={storefrontBranding}
              pageTemplateConfig={storefrontThemePageConfigs.detail}
            />
          )}
        />
        <Route path="/custom-service" element={withBootstrapGuard(<OemPrivateLabelPage apiBaseUrl={apiBaseUrl} storefrontLocale={storefrontLocale} />)} />
        <Route path="/oem-private-label" element={<Navigate to="/custom-service" replace />} />
        <Route path="/solutions" element={withBootstrapGuard(<SolutionsPage apiBaseUrl={apiBaseUrl} storefrontLocale={storefrontLocale} />)} />
        <Route path="/about" element={withBootstrapGuard(<AboutPage apiBaseUrl={apiBaseUrl} storefrontLocale={storefrontLocale} />)} />
        <Route
          path="/contact"
          element={withBootstrapGuard(
            <ContactPage
              apiBaseUrl={apiBaseUrl}
              storefrontLocale={storefrontLocale}
              storefrontContacts={storefrontBranding}
            />
          )}
        />
        <Route path="/news" element={withBootstrapGuard(<NewsPage apiBaseUrl={apiBaseUrl} storefrontLocale={storefrontLocale} />)} />
        <Route path="/blog" element={withBootstrapGuard(<NewsPage apiBaseUrl={apiBaseUrl} storefrontLocale={storefrontLocale} />)} />
        <Route
          path="/login"
          element={withBootstrapGuard(
            hasAdminAccess ? (
              <Navigate to="/admin" replace />
            ) : (
              hasLoginPageComponent ? (
                <LoginPage
                  apiBaseUrl={apiBaseUrl}
                  storefrontLocale={storefrontLocale}
                  allowRegistration={registrationEnabled}
                />
              ) : (
                <InternalFaultPage
                  title="Internal Site Error"
                  detail="The login page failed to load. Please refresh and try again."
                />
              )
            )
          )}
        />
        <Route
          path="/register"
          element={withBootstrapGuard(
            registrationEnabled ? (
              <Navigate to="/login?auth=register" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          )}
        />
        <Route
          path="/admin/login"
          element={withBootstrapGuard(<Navigate to={effectiveAdminToken ? "/admin" : "/login"} replace />)}
        />
        <Route
          path="/admin"
          element={withBootstrapGuard(
            hasAdminAccess ? (
              <AdminDashboardPage
                apiBaseUrl={apiBaseUrl}
                adminToken={effectiveAdminToken}
                adminRole={effectiveAdminRole || "customer"}
                locale={adminLocale}
                onChangeLocale={setAdminLocale}
                onSessionExpired={handleSessionExpired}
                onRestrictedAccess={handleRestrictedAdminAccess}
                onLogout={handleAdminLogout}
              />
            ) : hasPendingAdminSync ? null : effectiveAdminToken ? (
              <Navigate to="/products" replace />
            ) : (
              <Navigate to="/login?next=%2Fadmin" replace />
            )
          )}
        />
        <Route path="/media/oss" element={null} />
        <Route path="/uploads/*" element={null} />
        <Route path="/api/uploads/*" element={null} />
        <Route path="/assets/company-images/*" element={null} />
        <Route path="/api/assets/company-images/*" element={null} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {bootstrapStatus.loading && showBootstrapLoadingScreen && !isDeveloperEntryRoute ? <RouteLoadingOverlay /> : null}

      {!isAdminRoute && !isDeveloperEntryRoute && !isMediaAssetRoute && !isLoginRoute ? (
        <StorefrontFooter
          navItems={storefrontNavItems}
          logoSrc={storefrontBranding.logoUrl || "/assets/about/logo.png"}
          logoAlt={storefrontBranding.logoAlt || "Loya Sport logo"}
          storefrontTitle={storefrontBranding.storefrontTitle || "LOYA SPORT"}
          contactEmail={storefrontBranding.contactEmail}
          contactPhone={storefrontBranding.contactPhone}
          contactWhatsapp={storefrontBranding.contactWhatsapp}
        />
      ) : null}

      {!isAdminRoute && !isDeveloperEntryRoute && !isMediaAssetRoute ? (
        <div className="floating-contact-rail" aria-label="Quick contact tools">
          {whatsappLink ? (
            <a
              className="floating-action floating-action-whatsapp"
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Contact us on WhatsApp"
              title="WhatsApp"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19.05 4.94A9.9 9.9 0 0 0 12.03 2c-5.5 0-9.97 4.46-9.97 9.96 0 1.75.46 3.46 1.33 4.96L2 22l5.22-1.36a9.93 9.93 0 0 0 4.8 1.23h.01c5.49 0 9.97-4.47 9.97-9.96a9.9 9.9 0 0 0-2.95-6.97ZM12.03 20.19h-.01a8.26 8.26 0 0 1-4.21-1.15l-.3-.18-3.1.81.83-3.02-.2-.31a8.22 8.22 0 0 1-1.27-4.39c0-4.57 3.72-8.29 8.3-8.29 2.22 0 4.31.86 5.87 2.43a8.23 8.23 0 0 1 2.43 5.87c0 4.58-3.72 8.3-8.29 8.3Zm4.55-6.18c-.25-.12-1.47-.72-1.7-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.78.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.22-1.46-1.37-1.7-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.42-.56-.43h-.47c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.74 2.66 4.22 3.73.59.26 1.06.41 1.42.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.17-.48-.29Z" fill="currentColor"/>
              </svg>
            </a>
          ) : null}
          <button
            type="button"
            className="floating-action floating-action-top"
            aria-label="Back to top"
            title="Back to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6.7 14.7a1 1 0 0 1 0-1.41l4.59-4.59a1 1 0 0 1 1.41 0l4.6 4.59a1 1 0 1 1-1.42 1.42L12 10.82 8.11 14.7a1 1 0 0 1-1.41 0Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      ) : null}

      {showSessionWarning && isAdminRoute ? (
        <div className="session-modal-backdrop">
          <div className="session-modal">
            <h3>{uiText.expiringTitle}</h3>
            <p>{uiText.expiringBody}</p>
            <div className="row-actions">
              <button
                type="button"
                className="button"
                onClick={renewAdminSession}
                disabled={renewing}
              >
                {renewing ? uiText.renewing : uiText.extend}
              </button>
              <button
                type="button"
                className="button ghost"
                onClick={handleAdminLogout}
              >
                {uiText.logout}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
